<template>
  <div
    v-for="seat in filteredLegendSeats"
    :key="seat.name + seat.customSeatTypeColor"
    :class=" {
      'col-auto row no-wrap justify-center top-legend': topLegend,
      'row justify-between items-center': !topLegend,
      'column': vertical,
      'q-pa-sm borders rounded-borders': dialog
    }"
    data-test-id="legend-seats-item"
    @mouseenter="emitHover(seat)"
    @mouseleave="emitHover(null)"
  >
    <div
      :style="{
        'max-width': setWidth(seat.seatType),
        'min-width': setWidth(seat.seatType),
        'height': topLegend ? '20px' : '40px'
      }"
      class="seat col-auto"
    >
      <div
        class="seat-content"
        :class="[seat.class, seat.seatType]"
        :style="{ color: seat.customSeatTypeColor ? `${seat.customSeatTypeColor} !important` : undefined }"
      >
        <q-icon
          v-if="showSeatIcon(seat)"
          :class=" {
            'absolute selected-icon': seat.name === AVAILABILITY_TYPES.SELECTED,
            'absolute-center': seat.name !== AVAILABILITY_TYPES.SELECTED,
          }"
          :style="{
            top: seat.name === AVAILABILITY_TYPES.SELECTED ? '1%' : undefined,
            'padding-bottom': seat.name !== AVAILABILITY_TYPES.SELECTED ? '5px' : undefined
          }"
          :color="seatIconColor(seat)"
          :name="seatIcon(seat)"
          :size="topLegend ? '8px' : '12px'"
        />
        <svg viewBox="0 0 220 220">
          <use :xlink:href="'/seats.svg#' + seat.seatType" />
        </svg>
        <div
          v-if="seat.name === AVAILABILITY_TYPES.SELECTED"
          class="row justify-center q-pt-xs"
        >
          <div
            class="seat-text text-responsive"
            :style="{
              top: vertical || !topLegend ? '25%' : '20%',
              'font-size': vertical || !topLegend ? '10px' : '8px'
            }"
          >
            A1
          </div>
        </div>
      </div>
    </div>
    <div
      class="text-caption q-mt-xs q-ml-xs top-legend-seat-name text-responsive col"
      :class="{
        'row': topLegend,
        'column': !topLegend,
        'text-right q-ml-sm': !topLegend && !vertical && !showPrice
      }"
      :style="{ 'font-size': vertical || !topLegend ? '1.4vh' : '1.2vh' }"
    >
      <span
        class="block col-auto text-capitalize"
        :class="{ 'ellipsis q-mr-xs': topLegend }"
        :style="{ 'max-width': topLegend ? '120px' : undefined }"
      >
        {{ seat.label || seat.name }}
      </span>
      <span
        v-if="showPrice && seat.price"
        class="text-weight-bold col"
      >
        {{ seat.price }}
      </span>
    </div>
  </div>
</template>

<script>

import i18n from 'src/i18n'
import { AVAILABILITY_TYPES, SEAT_TYPES } from 'src/models/screen/SeatChart'
import seatSaleRestrictionService from 'src/services/seats/SeatSaleRestrictionService'

const DEFAULT_LEGEND_SEATS = [
  {
    name: i18n.global.t('seatChart.types.regular'),
    seatType: SEAT_TYPES.REGULAR_SEAT,
    topLegend: true,
    price: null
  },
  {
    name: i18n.global.t('seatChart.types.recliner'),
    seatType: SEAT_TYPES.RECLINER_SEAT,
    topLegend: true,
    price: null
  },
  {
    name: i18n.global.t('seatChart.types.couch'),
    seatType: SEAT_TYPES.COUCH_LEFT,
    topLegend: true,
    price: null
  },
  {
    name: i18n.global.t('seatChart.types.couch'),
    seatType: SEAT_TYPES.COUCH_RIGHT,
    topLegend: true,
    price: null
  },
  {
    name: i18n.global.t('seatChart.types.couch'),
    seatType: SEAT_TYPES.COUCH_CENTER,
    topLegend: true,
    price: null
  },
  {
    name: i18n.global.t('seatChart.types.couch'),
    seatType: SEAT_TYPES.COUCH_CORNER_LEFT,
    topLegend: true,
    price: null
  },
  {
    name: i18n.global.t('seatChart.types.couch'),
    seatType: SEAT_TYPES.COUCH_CORNER_RIGHT,
    topLegend: true,
    price: null
  },
  {
    name: i18n.global.t('seatChart.types.couch'),
    seatType: SEAT_TYPES.COUCH_VERTICAL_EAST_LEFT,
    topLegend: true,
    price: null
  },
  {
    name: i18n.global.t('seatChart.types.couch'),
    seatType: SEAT_TYPES.COUCH_VERTICAL_EAST_CENTER,
    topLegend: true,
    price: null
  },
  {
    name: i18n.global.t('seatChart.types.couch'),
    seatType: SEAT_TYPES.COUCH_VERTICAL_WEST_CENTER,
    topLegend: true,
    price: null
  },
  {
    name: i18n.global.t('seatChart.types.couch'),
    seatType: SEAT_TYPES.COUCH_VERTICAL_WEST_CENTER,
    topLegend: true,
    price: null
  },
  {
    name: i18n.global.t('seatChart.types.wheelchair'),
    seatType: SEAT_TYPES.WHEELCHAIR,
    topLegend: true,
    price: null
  },
  {
    name: i18n.global.t('seatChart.types.companion'),
    seatType: SEAT_TYPES.COMPANION,
    topLegend: true,
    price: null
  },
  {
    name: i18n.global.t('seatChart.types.table'),
    seatType: SEAT_TYPES.TABLE
  },
  {
    name: i18n.global.t('seatChart.types.table'),
    seatType: SEAT_TYPES.TABLE_LEFT
  },
  {
    name: i18n.global.t('seatChart.types.table'),
    seatType: SEAT_TYPES.TABLE_RIGHT
  },
  {
    name: i18n.global.t('seatChart.types.table'),
    seatType: SEAT_TYPES.TABLE_CENTER
  },
  {
    name: AVAILABILITY_TYPES.SELECTED,
    label: i18n.global.t('actions.selected'),
    seatType: SEAT_TYPES.REGULAR_SEAT,
    class: 'selected'
  },
  {
    name: AVAILABILITY_TYPES.RESERVED,
    label: i18n.global.t('status.reserved'),
    seatType: SEAT_TYPES.REGULAR_SEAT,
    class: 'reserved',
    topLegend: true,
    required: true
  },
  {
    name: AVAILABILITY_TYPES.UNAVAILABLE,
    label: i18n.global.t('status.unavailable'),
    seatType: SEAT_TYPES.REGULAR_SEAT,
    class: 'unavailable',
    required: true
  },
  {
    name: AVAILABILITY_TYPES.SOCIAL_DISTANCING,
    label: i18n.global.t('seatChart.socialDistancing'),
    seatType: SEAT_TYPES.REGULAR_SEAT,
    class: 'reserved social-distancing'
  },
  {
    name: AVAILABILITY_TYPES.PRIOR_SEATS,
    label: i18n.global.t('seatChart.priorSeats'),
    seatType: SEAT_TYPES.REGULAR_SEAT,
    class: 'previous-selection'
  }
]
export default {
  name: 'LegendSeatsItem',
  configuration: ['currentSite', 'userClasses', 'ticketTypes'],
  props: {
    topLegend: {
      type: Boolean,
      required: false,
      default: false
    },
    seatChartArray: {
      type: Array,
      required: true
    },
    previousSeatNames: {
      type: Array,
      required: true
    },
    seatChartOptions: {
      type: Object,
      required: true
    },
    vertical: {
      type: Boolean,
      required: false,
      default: false
    },
    showing: {
      type: Object,
      required: true
    },
    dialog: {
      type: Boolean,
      required: false,
      default: false
    },
    ticketOrderItems: {
      type: Array,
      default: () => ([]),
      required: false
    },
    packageOrderItems: {
      type: Array,
      default: () => ([]),
      required: false
    },
    packages: {
      type: Array,
      default: () => ([]),
      required: false
    },
    showPrice: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['hover-seat'],
  computed: {
    availableTicketTypes () {
      // If this.showing exists and has priceCardTicketTypes
      if (Array.isArray(this.showing?.priceCardTicketTypes) && !_.isEmpty(this.showing?.priceCardTicketTypes)) {
        return this.showing.priceCardTicketTypes
      }

      // Fallback to this.ticketTypes.data
      if (_.isEmpty(this.ticketTypes?.data)) {
        return [] // Return empty array to avoid errors
      }

      return _.filter(this.ticketTypes.data, ticketType => ticketType.isActive && ticketType.isAvailable)
    },
    ticketPrice () {
      const prices = {}
      _.each(this.ticketOrderItems, ticketOrderItem => {
        prices[ticketOrderItem.ticket.ticketType.id] = ticketOrderItem.displayPrice
      })
      return prices
    },
    packagePrice () {
      const prices = {}

      _.each(this.packageOrderItems, packageOrderItem => {
        prices[packageOrderItem.itemPackageId] ||= { displayPrice: 0, displayTotal: 0, discountAmount: 0, bookingFeeAmount: 0, bookingFeeNames: [] }
        prices[packageOrderItem.itemPackageId].displayTotal += packageOrderItem.displayPrice - packageOrderItem.displayRedemptionTotal
        prices[packageOrderItem.itemPackageId].displayPrice += packageOrderItem.displayPrice
        prices[packageOrderItem.itemPackageId].discountAmount += packageOrderItem.displayRedemptionTotal
        prices[packageOrderItem.itemPackageId].bookingFeeAmount += packageOrderItem.includeBookingFeeInPrice ? packageOrderItem.bookingFeeDisplayPrice : 0
        const bookingFeeName = (packageOrderItem.bookingFeeType || packageOrderItem.localBookingFeeType)?.name
        if (!_.isNil(bookingFeeName)) {
          prices[packageOrderItem.itemPackageId].bookingFeeNames = _.union(prices[packageOrderItem.itemPackageId].bookingFeeNames, [bookingFeeName])
        }
      })
      return prices
    },
    allLegendSeats () {
      // Get default ticket type and price
      const defaultOnlineUserClassId = this.userClasses.defaultOnlineUserClass.id

      const defaultTicketType = _.find(this.availableTicketTypes, ticketType => ticketType.userClassId === defaultOnlineUserClassId) ||
        _.find(this.availableTicketTypes, ticketType => _.isNil(ticketType.userClassId))

      // Get default price (includes booking fee if applicable)
      const defaultPrice = (this.ticketPrice[defaultTicketType?.id] ?? null)

      // Map custom seat types from seat chart
      let customSeatTypes = _.map(
        _.filter(_.flatten(this.seatChartArray), seat => seat.customSeatType || !_.isNil(seat.areaCategoryId)),
        seatType => {
          // Determine if price is shown along with seat type

          // Determine if specific ticket types are restricted for this seat
          const matchingTicketTypeItems = !_.isNil(seatType.areaCategoryId)
            ? _.filter(this.availableTicketTypes, ticketType => {
              return seatSaleRestrictionService.isSeatSaleAllowed(seatType, ticketType)
            })
            : []

          const matchingTicketTypeItem = _.first(matchingTicketTypeItems)

          // Calculate final price (includes booking fee if applicable)
          const price = (this.ticketPrice[matchingTicketTypeItem?.id] ?? this.packagePrice[this.packages[0]?.id]?.displayPrice ?? defaultPrice)

          // Use ticket type name or custom seat type name
          const seatName = seatType.customSeatTypeName || matchingTicketTypeItem?.name

          return {
            name: seatName || this.$i18n.tc('seatChart.customSeatType', { seatType: seatType.seatType.replace(/-/g, ' ') }),
            seatType: seatType.seatType,
            customSeatTypeColor: seatType.customSeatTypeColor,
            price: price != null ? `${this.$i18n.n(price, 'currency')}` : null,
            topLegend: true,
            ticketTypeIds: seatType.customSeatTypeName ? null : _.map(matchingTicketTypeItems, 'id')
          }
        }
      )
      // Remove duplicate seat types by name
      customSeatTypes = _.uniqBy(customSeatTypes, seatType =>
        [seatType.name.toLowerCase()].join()
      )

      return _.filter(_.union(customSeatTypes, structuredClone(DEFAULT_LEGEND_SEATS)), seat => {
        // Always include required seats
        if (seat.required) return true
        // Include social distancing seats if enabled
        if (seat.name === AVAILABILITY_TYPES.SOCIAL_DISTANCING) {
          return this.seatChartOptions.socialDistancing
        }
        // Include previous seats if available
        if (seat.name === AVAILABILITY_TYPES.PRIOR_SEATS) {
          return this.previousSeatNames?.length > 0
        }
        // Ignore aisle & available seats in legend
        if (seat.seatType === AVAILABILITY_TYPES.AISLE || seat.seatType === AVAILABILITY_TYPES.AVAILABLE) {
          return false
        }
        // Set default price if price null provided and default price is not null
        if (seat.price === null && this.showPrice && defaultPrice !== null) {
          seat.price = `${this.$i18n.n(defaultPrice, 'currency')}`
        }
        // Check if seat type matches any custom seat type or default seat type
        return _.some(_.flatten(this.seatChartArray), someSeat =>
          (someSeat.customSeatTypeName === seat.name) ||
          (!someSeat.customSeatType && someSeat.seatType === seat.seatType)
        )
      })
    },
    topLegendSeats () {
      return _.filter(this.allLegendSeats, seat => {
        if (seat.required) return true

        if (seat.customSeatTypeColor) return true

        if (!seat.topLegend) return false

        if (seat.name === AVAILABILITY_TYPES.PRIOR_SEATS) {
          return this.previousSeatNames && this.previousSeatNames.length
        }

        return _.some(_.flatten(this.seatChartArray), someSeat => someSeat.seatType === seat.seatType)
      })
    },
    filteredLegendSeats () {
      const seats = this.topLegend ? this.topLegendSeats : this.allLegendSeats

      // Track seen seatTypes to avoid duplicates
      const seenTypes = new Set()

      const couchTypes = [
        SEAT_TYPES.COUCH_LEFT,
        SEAT_TYPES.COUCH_RIGHT,
        SEAT_TYPES.COUCH_CORNER_LEFT,
        SEAT_TYPES.COUCH_CORNER_RIGHT
      ]
      const couchCornerTypes = [
        SEAT_TYPES.COUCH_VERTICAL_EAST_CENTER,
        SEAT_TYPES.COUCH_VERTICAL_EAST_LEFT,
        SEAT_TYPES.COUCH_VERTICAL_WEST_CENTER,
        SEAT_TYPES.COUCH_VERTICAL_WEST_RIGHT
      ]
      const tableTypes = [SEAT_TYPES.TABLE_LEFT, SEAT_TYPES.TABLE_RIGHT]

      return seats
        .map(seat => {
          let seatType = seat.seatType

          if (couchCornerTypes.includes(seatType)) {
            seatType = SEAT_TYPES.COUCH_CORNER_SET
          } else if (couchTypes.includes(seatType)) {
            seatType = SEAT_TYPES.COUCH
          } else if (tableTypes.includes(seatType)) {
            seatType = SEAT_TYPES.TABLE_SET
          }

          return {
            ...seat,
            seatType
          }
        })
        .filter(seat => {
          // Always include regular-seat, recliner-seat, no deduplication
          if (seat.seatType === SEAT_TYPES.REGULAR_SEAT) {
            return true
          }
          if (seat.seatType === SEAT_TYPES.RECLINER_SEAT) {
            return true
          }

          // For other seatTypes, include only if not seen before
          if (seenTypes.has(seat.seatType)) {
            return false // Skip duplicates
          }
          seenTypes.add(seat.seatType)
          return true // Keep first occurrence
        })
    }
  },
  created () {
    _.extend(this, { AVAILABILITY_TYPES, SEAT_TYPES })
  },
  methods: {
    setWidth (seatType) {
      if (seatType === SEAT_TYPES.COUCH) {
        return this.topLegend ? '40px' : '60px'
      }
      if (seatType === SEAT_TYPES.COUCH_CORNER_SET) {
        return this.topLegend ? '30px' : '50px'
      }
      if (seatType === SEAT_TYPES.TABLE_SET) {
        return this.topLegend ? '30px' : '45px'
      }
      return this.topLegend ? '20px' : '35px'
    },
    showSeatIcon (seat) {
      return seat.reserved || !seat.available || (seat.name && seat.name !== AVAILABILITY_TYPES.AISLE && seat.name !== AVAILABILITY_TYPES.AVAILABLE)
    },
    seatIcon (seat) {
      if (seat.name === AVAILABILITY_TYPES.SELECTED) {
        return 'fas fa-check'
      } else if (seat.name === AVAILABILITY_TYPES.RESERVED) {
        return 'fas fa-user'
      } else if (seat.name === AVAILABILITY_TYPES.UNAVAILABLE) {
        return 'fas fa-user-slash'
      }
      return undefined
    },
    seatIconColor (seat) {
      if (seat.name === AVAILABILITY_TYPES.SELECTED) return this.$getColor(this.currentSite, 'seatSelected')
      if (seat.name === AVAILABILITY_TYPES.RESERVED || seat.name === AVAILABILITY_TYPES.SOCIAL_DISTANCING) return 'negative'
      return 'responsive-grey-5'
    },
    emitHover (seat) {
      this.$emit('hover-seat', seat)
    }

  }
}

</script>

<style lang="scss" scoped>
.seat {
    flex-grow: 1;
    flex-shrink: 1;
    width: 20px;
    fill-opacity: 0;
  }
  .seat-content {
    width: 100%;
    position: relative;
  }
  svg {
    width: 100%;
    height: auto;
  }

  .absolute-center{
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    margin: auto;
    transform: none;
  }
  .seat-swap-mode:not(.override) {
    pointer-events: none;
  }
  .seat-text {
    font-weight: 800;
    text-align: center;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    user-select: none;
  }
  .seat-content {
    padding: 20% 5%;
  }
  .seat-content.regular-seat {
    padding: 20% 10%;
  }

  .seat-content.couch-left, .seat-content.couch-corner-left {
    padding: 20% 0% 20% 10%;
  }

  .seat-content.couch-vertical-east-left {
    padding: 40% 10% 0% 10%;
  }

  .seat-content.couch-right, .seat-content.couch-corner-right {
    padding: 20% 10% 20% 0%;
  }

  .seat-content.couch-vertical-west-right {
    padding: 40% 10% 0% 10%;
  }

  .seat-content.couch-center {
    padding: 20% 0%;
  }

  .seat-content.couch-vertical-east-center {
    margin-top: -10%;
    margin-bottom: -20%;
    padding: 0%;
  }

  .seat-content.couch-vertical-west-center {
    margin-top: -10%;
    margin-bottom: -20%;
    padding: 0%;
  }
  .seat-content.couch, .seat-content.couch-corner-set {
    margin-top: -20%;
    margin-bottom: -20%;
    padding: 0%;
  }
  .seat-content.table-set {
    margin-top: -10%;
    margin-bottom: -10%;
    padding: 0%;
  }
  .seat-content.table, .seat-content.table-left, .seat-content.table-right, .seat-content.table-center {
    padding: 30% 0 0 0;
  }

  .seat-content.table-left {
    padding: 30% 0 0 0;
  }

  .seat-content.table-right {
    padding: 30% 0 0 0;
  }

  .seat-content.table-center {
    padding: 30% 0 0 0;
  }

  .show-scrollbars {
    overflow: auto;
  }

  .social-distancing {
    opacity: 0.4
  }

  .previous-selection {
    border: dashed blue 2px;
    margin: -2px 0px -2px -2px;
    width: 24px;
  }

  .top-legend .previous-selection {
    width: 29px;
    height: 35px;
  }

  .legend .previous-selection {
    width: 38px;
    height: 44px;
  }

  .top-legend-seat-name {
    font-size: 1.2vh;
    font-weight: 500;
  }
</style>
